import { Box, Divider, Grid, Typography, FormHelperText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { SEX_LIST } from '@/common/constants/common';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppEstimatedAgeTextField from '@/common/components/form-inputs/AppEstimatedAgeTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useGetStaffAllListQuery } from '@/api/common';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditReferralForICCMWForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();

		const defaultValues: DefaultValues<any> = {
			sex: null,
		};

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			referralDate: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			// receivingTherapy: Yup.boolean()
			// 	.required(t('errorMessage.pleaseSelect'))
			// 	.oneOf([true], t('errorMessage.pleaseSelect')),
			othersSpecify: Yup.string().when('others', {
				is: (others: string) => others,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			therapyValidation: Yup.string().test(
				'at-least-one-therapy',
				t('errorMessage.pleaseSelect'), // 错误提示
				function () {
					const {
						receivingTherapy,
						occupationalTherapy,
						outreaching,
						trainingServices,
					} = this.parent;

					if (receivingTherapy === true) {
						const hasSelected =
							occupationalTherapy === true ||
							outreaching === true ||
							trainingServices === true;

						return hasSelected;
					}

					// 如果 receivingTherapy 不为 true，跳过验证
					return true;
				}
			),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchOthers = methods.watch('others');
		const watchrReceivingTherapy = methods.watch('receivingTherapy');

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.receiptOfReferralForICCMW')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameEn'
									label={t('preAdmission.name')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='hkicNo'
									label={t('preAdmission.hkicNo')}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.sex')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='sex'
									row
									options={SEX_LIST}
								/>
							</Grid>
							<Grid size={6}>
								<AppEstimatedAgeTextField
									control={methods.control}
									label={t('preAdmission.age')}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									control={methods.control}
									name='referralDate'
									label={t(
										'preAdmission.referringAboveNamedServiceUserUouReferredOn'
									)}
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								/>
							</Grid>
						</Grid>

						<Divider sx={{ marginBottom: '16px' }} />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='h6' sx={{ fontWeight: 'bold' }}>
									{t('preAdmission.numServiceRendered')}
								</Typography>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='counsellingToServiceUser'
										label={t('preAdmission.counsellingToServiceUser')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='counsellingToFamily'
										label={t('preAdmission.memberCarerServiceUser')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='successfulEngagement'
										label={t(
											'preAdmission.supportDevelopmentalGroupsEducationalProgrammes'
										)}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='referralToCps'
										label={t('preAdmission.referralToCps')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='receivingTherapy'
										label={`${watchrReceivingTherapy ? '*' : ''}${t(
											'preAdmission.receivingTherapy'
										)}`}
										handleChange={() => {
											methods.trigger('therapyValidation');
										}}
									/>
								</Box>
								<Box display={'flex'} marginLeft={'32px'}>
									<SingleCheckbox
										disabled={!watchrReceivingTherapy}
										control={methods.control}
										name='occupationalTherapy'
										label={t('preAdmission.occupationalTherapy')}
										handleChange={() => {
											methods.trigger('therapyValidation');
										}}
									/>
								</Box>
								<Box display={'flex'} marginLeft={'32px'}>
									<SingleCheckbox
										disabled={!watchrReceivingTherapy}
										control={methods.control}
										name='outreaching'
										label={t('preAdmission.outreaching')}
										handleChange={() => {
											methods.trigger('therapyValidation');
										}}
									/>
								</Box>
								<Box display={'flex'} marginLeft={'32px'}>
									<SingleCheckbox
										disabled={!watchrReceivingTherapy}
										control={methods.control}
										name='trainingServices'
										label={t('preAdmission.trainingServices')}
										handleChange={() => {
											methods.trigger('therapyValidation');
										}}
									/>
								</Box>

								<FormHelperText color='error' style={{ color: '#d32f2f' }}>
									{methods.formState.errors?.therapyValidation
										? t('errorMessage.pleaseSelect')
										: ''}
								</FormHelperText>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='peerSupportService'
										label={t('preAdmission.peerSupportService')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='clinicalPsychologicalService'
										label={t('preAdmission.clinicalPsychologicalService')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='others'
										label={t('preAdmission.othersSpecify')}
										handleChange={() => {
											methods.setValue('othersSpecify', '');
											methods.clearErrors('othersSpecify');
										}}
									/>
									<AppTextField
										disabled={!watchOthers}
										sx={{ position: 'relative', top: '-10px' }}
										variantType={'standard'}
										control={methods.control}
										name='othersSpecify'
										label={`${t('preAdmission.specify')}${
											watchOthers ? '*' : ''
										}`}
									/>
								</Box>
							</Grid>
						</Grid>
						<Divider sx={{ marginBottom: '16px' }} />
					</form>
				</FormProvider>
			</>
		);
	}
);

EditReferralForICCMWForm.displayName = 'EditReferralForICCMWForm';

export default EditReferralForICCMWForm;
